import { useOktaAuth } from '@okta/okta-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';

import { isMagicLinkUserAtom } from '../../modules/login/atoms';
import { operatoryFilterAtom } from '../../modules/patientList/atoms';
import { mixpanelEvents, useTrackEvent } from '../../utils/mixpanel';

import { selectedVisitAtom } from './imageViewerMenu/atoms';

const logoutChannel = new BroadcastChannel('logout');

export const useHandleLogout = () => {
  const setSelectedVisit = useSetAtom(selectedVisitAtom);
  const setOperatories = useSetAtom(operatoryFilterAtom);

  const { trackEvent } = useTrackEvent();
  const queryClient = useQueryClient();
  const { oktaAuth } = useOktaAuth();

  const isMagicLinkUser = useAtomValue(isMagicLinkUserAtom);

  const logout = async (clearIdp = false, payload?: Record<string, any>) => {
    if (isMagicLinkUser) {
      trackEvent(
        mixpanelEvents.magicLinkRedirectToLogin,
        {
          cause: 'Logout method',
        },
        undefined,
        undefined,
        false,
      );
    }
    // Don't double write to logout for now until we have unauthenticated events functioning
    trackEvent(mixpanelEvents.logout, payload, undefined, undefined, false);
    setSelectedVisit(null);
    setOperatories([]);
    await oktaAuth.signOut({
      postLogoutRedirectUri: window.location.hostname.includes('preview')
        ? 'https://assist.dev.videahealth.io'
        : window.location.origin,
    });
    // Clear only jotai-prefixed localStorage and sessionStorage items
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('jotai-') && key !== 'jotai-selectedLocale') {
        localStorage.removeItem(key);
      }
    });
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith('jotai-')) {
        sessionStorage.removeItem(key);
      }
    });

    // Clear IdP storage only on manual logout
    if (clearIdp) {
      localStorage.removeItem('idp');
    }
    queryClient.clear();
    logoutChannel.postMessage('logout');
    if (window?.__mswStop) {
      try {
        window.__mswStop();
      } catch {
        /* fail silently - we don't care if mswStop does not work it's dev-only */
      }
    }
  };
  return {
    logout,
    logoutAllTabs: () => {
      logoutChannel.onmessage = async () => {
        if (isMagicLinkUser) {
          trackEvent(
            mixpanelEvents.magicLinkRedirectToLogin,
            {
              cause: 'Logout all tabs',
            },
            undefined,
            undefined,
            false,
          );
        }
        await logout();
      };
    },
  };
};
